import {
  Button,
  Col,
  Drawer,
  Dropdown,
  Flex,
  Image,
  Menu,
  MenuProps,
  Row,
  Tooltip,
} from "antd";
import { navbarItems } from "@configurations";
import { useLocation } from "react-router-dom";
import performa from "../assets/Performa.png";
import exam from "../assets/exams.png";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronFirst, ChevronLast, LogOut, MenuIcon, Settings } from "lucide-react";
import { useKeycloak } from "@react-keycloak/web";
import { SettingsDrawer } from "@components";
import { User, TenantSettings } from "@types";

interface Props {
  user: User;
  tenantSettings?: TenantSettings;
  isLoading: boolean;
  collapsed?: boolean;
  setCollapsed?: (v: boolean) => void;
}

export const Navbar = ({user, tenantSettings, isLoading, collapsed = false, setCollapsed}: Props) => {
  const [current, setCurrent] = useState("");
  const { t } = useTranslation();
  const location = useLocation();
  const { keycloak } = useKeycloak();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false);
  const toggleCollapsed = () => {
    setCollapsed && setCollapsed(!collapsed);
  };
  const fullName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  const initials = `${(user?.firstName ?? "").charAt(0)}${(user?.lastName ?? "").charAt(0)}`.toUpperCase();


  const items: MenuProps["items"] = [
    {
      icon: <Settings />,
      label: t("settings"),
      onClick: () => setIsSettingsDrawerOpen(true),
      key: "1",
    },
    {
      danger: true,
      icon: <LogOut />,
      label: t("logout"),
      onClick: () => keycloak.logout(),
      key: "3",
    },
  ];

  useEffect(() => {
    const path = location.pathname;
    
    if (path === `/${window.location.pathname.split('/')[1]}/` || path === `/${window.location.pathname.split('/')[1]}`) {
      setCurrent("dashboard");
    } else if (path.includes("/exam-periods") || path.includes("/edit-exam-period")) {
      setCurrent("exam-periods");
    } else if (path.includes("/exams") || path.includes("/new-exam") || path.includes("/edit-exam")) {
      setCurrent("home");
    } else if (path.includes("/reports")) {
      setCurrent("reports");
    } else if (path.includes("/bank-pool")) {
      setCurrent("bank-pool");
    } else if (path.includes("/categories")) {
      setCurrent("categories");
    } else if (path.includes("/users")) {
      setCurrent("users");
    } else if (path.includes("/grading/pending-corrections")) {
      setCurrent("pending-corrections");
    } else if (path.includes("/quiz-wizard")) {
      setCurrent("quiz-wizard");
    }
  }, [location]);

  const onClick: MenuProps["onClick"] = (e) => {
    if (isDrawerOpen) {
      setIsDrawerOpen(false);
    }
    setCurrent(e.key);
  };

  return (
    <div >
      <Row>
        <Col
          xxl={24}
          xl={24}
          lg={24}
          md={0}
          sm={0}
          xs={0}
          style={{ width: "100%" }}
        >
          <Flex vertical justify="space-between" style={{ width: "100%", height: "100vh"}}>
            {/* Top: Logo */}
            <Flex align="center" justify={collapsed ? "center" : "start"} style={{ paddingLeft: collapsed ? 0 : 8, paddingTop: 8 }}>
              <Image
                width={48}
                style={{ borderRadius: 8 }}
                src={performa}
                preview={false}
              />

            </Flex>

            {/* Middle: Menu with its own scroll */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              <Menu
                selectedKeys={[current]}
                mode="inline"
                onClick={onClick}
                inlineCollapsed={collapsed}
                items={navbarItems(user, tenantSettings)}
              />
            </div>

            {/* Bottom: Profile control and collapse toggle pinned */}
            <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 8, alignItems: 'center' }}>
              <Dropdown trigger={["click"]} menu={{ items }}>
                {collapsed ? (
                  <Tooltip title={fullName} placement="right">
                    <Button type="text"  >
                      {initials || t("navbar.userInitial")}
                    </Button>
                  </Tooltip>
                ) : (
                  <Button type="text" style={{ width: "100%", textAlign: "left" }}>{fullName || t("navbar.user")}</Button>
                )}
              </Dropdown>
              <Button icon={collapsed ? <ChevronLast /> : <ChevronFirst />} onClick={toggleCollapsed} type="text" aria-label="Toggle sidebar" style={{ width: "100%", textAlign: collapsed ? "center" : "left" }}/>
            </div>
          </Flex>
        </Col>
        <Col md={24} sm={24} xs={24} lg={0} xl={0} xxl={0}>
          <Flex
            justify="space-between"
            align="center"
            style={{ width: "100%" }}
          >
            <Flex align="center" gap={"small"}>
              <Image
                width={48}
                style={{ borderRadius: 8 }}
                src={performa}
                preview={false}
              />
              <Button
                onClick={() => setIsDrawerOpen(true)}
                type="text"
                size="large"
                icon={<MenuIcon style={{ width: 32, height: 32 }} />}
              />
            </Flex>
            
          </Flex>
        </Col>
      </Row>
      <Drawer
        id="drawer"
        title={t("settings")}
        open={isSettingsDrawerOpen}
        placement="right"
        width={600}
        styles={{
          content: {
            borderRadius: 16,
          },
        }}
        loading={isLoading}
        onClose={() => setIsSettingsDrawerOpen(false)}
      >
        <SettingsDrawer
          setIsModalOpen={setIsSettingsDrawerOpen}
          user={user!}
        />
      </Drawer>

      <Drawer
        open={isDrawerOpen}
        width={600}
        placement="left"
        title={<Image width={128} src={exam} preview={false} />}
        onClose={() => setIsDrawerOpen(false)}
      >
        <Menu
          style={{ width: "100%" }}
          selectedKeys={[current]}
          mode="inline"
          onClick={onClick}
          items={navbarItems(user, tenantSettings)}
        />
      </Drawer>
    </div>
  );
};
